// Client-side image downscaling for chat uploads.
//
// Kids photograph work on phones that produce multi-MB images; sending those
// raw wastes bandwidth and vision tokens. We cap the longest edge and re-encode
// as JPEG before upload. Runs entirely in the browser (canvas), so nothing
// large ever leaves the device.

const MAX_EDGE = 1024; // px — plenty for the vision model to read handwriting
const JPEG_QUALITY = 0.85;

/**
 * Downscale an image File to a JPEG Blob no larger than MAX_EDGE on its longest
 * side. Returns the original file untouched if it can't be decoded (defensive —
 * the upload still works, just un-shrunk).
 */
export async function downscaleImage(file: File): Promise<Blob> {
  try {
    const bitmap = await createImageBitmap(file);
    const { width, height } = bitmap;
    const scale = Math.min(1, MAX_EDGE / Math.max(width, height));

    // Already small enough — keep the original bytes.
    if (scale === 1) {
      bitmap.close?.();
      return file;
    }

    const canvas = document.createElement('canvas');
    canvas.width = Math.round(width * scale);
    canvas.height = Math.round(height * scale);
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      bitmap.close?.();
      return file;
    }
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close?.();

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY)
    );
    return blob ?? file;
  } catch {
    return file;
  }
}
